import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export function Combobox({ options, value, onValueChange, placeholder, searchPlaceholder, emptyText }) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  // Ensure options is always an array to prevent "in null" errors
  // Also filter out any invalid options (null, undefined, or missing value/label)
  const safeOptions = React.useMemo(() => {
    if (!options || !Array.isArray(options)) return []
    return options.filter(opt => opt && opt.value !== undefined && opt.label !== undefined)
  }, [options])
  
  // Filter options based on search text
  const filteredOptions = React.useMemo(() => {
    if (!search) return safeOptions
    
    const searchLower = search.toLowerCase()
    return safeOptions.filter(option => {
      // If option has searchText property, use it (for custom search like matricule + nom + telephone)
      if (option.searchText) {
        return option.searchText.includes(searchLower)
      }
      // Otherwise, search in label
      return option.label?.toLowerCase().includes(searchLower)
    })
  }, [safeOptions, search])
  
  const selectedOption = safeOptions.find((option) => option?.value === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {selectedOption ? selectedOption.label : placeholder || "Sélectionner..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" style={{ width: 'var(--radix-popover-trigger-width)' }}>
        {open && (
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder={searchPlaceholder || "Rechercher..."} 
              value={search}
              onValueChange={setSearch}
            />
            <CommandEmpty>{emptyText || "Aucun résultat."}</CommandEmpty>
            <CommandGroup className="max-h-[300px] overflow-auto">
              {filteredOptions.length === 0 && search && (
                <div className="py-6 text-center text-sm text-gray-500">
                  Aucun résultat pour "{search}"
                </div>
              )}
              {filteredOptions.map((option) => {
                // Extra safety check for each option
                if (!option || !option.value || !option.label) return null
                
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      onValueChange(option.value === value ? "" : option.value)
                      setOpen(false)
                      setSearch("") // Reset search on selection
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.label}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  )
}
